import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
// import { databases } from "../../appwrite";
// import { ID, Permission, Role } from "appwrite";
import Input from "../../components/ui/Input";
import Button from "../../components/ui/Button";
import Select from "../../components/ui/Select";
import { cn } from "../../utils/cn";
import { useTranslation } from "react-i18next";
import { track } from "@vercel/analytics";

// const DATABASE_ID = "697ffbe8000416c190d2";
// const TABLE_ID = "demo";
const CALENDLY_URL = "https://calendly.com/mart-daniel97/30min";

const message = `New Strategy Call Booking:
- Age Range: {age_range}
- Location: {location}
- Fitness Goals: {fitness_goals}
- Why Now: {why_now}
- Coach & Client Qualities: {coach_client_qualities}
- Client Info: {client_info}
- Investment Readiness: {investment_readiness}
- Name: {name}
- Email: {email}
- Phone: {phone}
- LinkedIn: {linkedin}
`;

interface FormData {
  age_range: string;
  location: string;
  fitness_goals: string;
  why_now: string;
  coach_client_qualities: string;
  client_info: string;
  investment_readiness: string;
  name: string;
  email: string;
  phone: string;
  linkedin: string;
}

const BookACall = () => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>();

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      track("Signup", {
        method: "Strategy Call Form",
        email: data.email,
        phone: data.phone,
        name: data.name,
      });

      // Send data to Appwrite
      //   await databases.createDocument(
      //     DATABASE_ID,
      //     COLLECTION_ID,
      //     ID.unique(),
      //     data,
      //   );

      // TODO: Re-enable Appwrite database when needed
      // await databases.createRow(DATABASE_ID, TABLE_ID, ID.unique(), data, [
      //   Permission.read(Role.any()),
      // ]);

      const telegramMessage = message
        .replace("{age_range}", data.age_range)
        .replace("{location}", data.location)
        .replace("{fitness_goals}", data.fitness_goals)
        .replace("{why_now}", data.why_now)
        .replace("{coach_client_qualities}", data.coach_client_qualities)
        .replace("{client_info}", data.client_info)
        .replace(
          "{investment_readiness}",
          investmentOptions.find(
            (option) => option.value === data.investment_readiness,
          )?.label || data.investment_readiness,
        )
        .replace("{name}", data.name)
        .replace("{email}", data.email)
        .replace("{phone}", data.phone)
        .replace("{linkedin}", data.linkedin);

      const telegramResponse = await fetch("/api/send-telegram", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ message: telegramMessage }),
      });

      if (!telegramResponse.ok) {
        console.warn("Failed to send Telegram notification, but continuing...");
      }

      window.location.href = CALENDLY_URL;
    } catch (error: any) {
      console.error(t("bookACall.errorSubmittingForm"), error);
      setSubmitError(error.message || t("bookACall.submitError"));
      setIsSubmitting(false);
    }
  };

  const investmentOptions = [
    {
      value: "A",
      label: t("bookACall.investmentOptions.A"),
    },
    {
      value: "B",
      label: t("bookACall.investmentOptions.B"),
    },
    {
      value: "C",
      label: t("bookACall.investmentOptions.C"),
    },
    {
      value: "D",
      label: t("bookACall.investmentOptions.D"),
    },
  ];

  const ageOptions = [
    { value: "18-21", label: "18-21" },
    { value: "22-25", label: "22-25" },
    { value: "26-29", label: "26-29" },
    { value: "30+", label: "30+" },
  ];

  return (
    <div className="min-h-screen bg-background py-12 px-4 sm:px-6 lg:px-8 flex justify-center">
      <div className="w-full max-w-2xl space-y-8 bg-card/50 p-6 sm:p-10 rounded-xl backdrop-blur-sm shadow-lg">
        <div className="text-center space-y-4">
          <h1 className="text-4xl font-bold tracking-tight text-foreground">
            {t("bookACall.title")}
          </h1>
          <p className="text-muted-foreground text-lg">
            {t("bookACall.description")}
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8 gap-y-3">
          {/* Age Range */}
          <div className="space-y-2">
            <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-foreground">
              {t("bookACall.ageRange")}
              <span className="text-destructive ml-1">*</span>
            </label>
            <Controller
              name="age_range"
              control={control}
              rules={{ required: t("bookACall.ageRangeRequired") }}
              render={({ field }) => (
                <Select
                  {...field}
                  options={ageOptions}
                  placeholder={t("bookACall.agePlaceholder")}
                  error={errors.age_range?.message}
                  onChange={(value: string) => field.onChange(value)}
                />
              )}
            />
          </div>

          {/* Location */}
          <Input
            label={t("bookACall.location")}
            required
            placeholder={t("bookACall.locationPlaceholder")}
            error={errors.location?.message}
            {...register("location", {
              required: t("bookACall.locationRequired"),
            })}
          />

          {/* Fitness Goals */}
          <div className="space-y-2">
            <label className="text-sm font-medium leading-none text-foreground">
              {t("bookACall.fitnessGoals")}
              <span className="text-destructive ml-1">*</span>
            </label>
            <textarea
              className={cn(
                "flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
                errors.fitness_goals &&
                  "border-destructive focus-visible:ring-destructive",
              )}
              {...register("fitness_goals", {
                required: t("bookACall.fitnessGoalsRequired"),
              })}
            />
            {errors.fitness_goals && (
              <p className="text-sm text-destructive">
                {errors.fitness_goals.message}
              </p>
            )}
          </div>

          {/* Why Now */}
          <div className="space-y-2">
            <label className="text-sm font-medium leading-none text-foreground">
              {t("bookACall.whyNow")}
              <span className="text-destructive ml-1">*</span>
            </label>
            <textarea
              className={cn(
                "flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
                errors.why_now &&
                  "border-destructive focus-visible:ring-destructive",
              )}
              {...register("why_now", {
                required: t("bookACall.whyNowRequired"),
              })}
            />
            {errors.why_now && (
              <p className="text-sm text-destructive">
                {errors.why_now.message}
              </p>
            )}
          </div>

          {/* Coach & Client Qualities */}
          <div className="space-y-2">
            <label className="text-sm font-medium leading-none text-foreground">
              {t("bookACall.coachClientQualities")}
              <span className="text-destructive ml-1">*</span>
            </label>
            <textarea
              className={cn(
                "flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
                errors.coach_client_qualities &&
                  "border-destructive focus-visible:ring-destructive",
              )}
              {...register("coach_client_qualities", {
                required: t("bookACall.coachClientQualitiesRequired"),
              })}
            />
            {errors.coach_client_qualities && (
              <p className="text-sm text-destructive">
                {errors.coach_client_qualities.message}
              </p>
            )}
          </div>

          {/* Client Info */}
          <div className="space-y-2">
            <label className="text-sm font-medium leading-none text-foreground">
              {t("bookACall.clientInfo")}
              <span className="text-destructive ml-1">*</span>
            </label>
            <textarea
              className={cn(
                "flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
                errors.client_info &&
                  "border-destructive focus-visible:ring-destructive",
              )}
              {...register("client_info", {
                required: t("bookACall.clientInfoRequired"),
              })}
            />
            {errors.client_info && (
              <p className="text-sm text-destructive">
                {errors.client_info.message}
              </p>
            )}
          </div>

          {/* Investment Readiness */}
          <div className="space-y-4">
            <label className="text-sm font-medium leading-none text-foreground block">
              {t("bookACall.investmentReadiness")}
              <span className="text-destructive ml-1">*</span>
            </label>
            <Controller
              name="investment_readiness"
              control={control}
              rules={{ required: t("bookACall.investmentReadinessRequired") }}
              render={({ field }) => (
                <div className="space-y-3">
                  {investmentOptions.map((option) => (
                    <label
                      key={option.value}
                      className={cn(
                        "flex items-start space-x-3 p-4 rounded-lg border border-input hover:bg-accent/50 cursor-pointer transition-colors",
                        field.value === option.value
                          ? "bg-accent border-primary ring-1 ring-primary"
                          : "bg-card",
                      )}
                    >
                      <input
                        type="radio"
                        value={option.value}
                        checked={field.value === option.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        className="mt-1 h-4 w-4 shrink-0 rounded-full border border-primary text-primary focus:ring-2 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-50"
                      />
                      <span className="text-sm leading-tight">
                        {option.label}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            />
            {errors.investment_readiness && (
              <p className="text-sm text-destructive">
                {errors.investment_readiness.message}
              </p>
            )}
          </div>

          {/* Contact Information */}
          <div className="space-y-4 border-t border-border pt-8">
            <h3 className="text-xl font-semibold">
              {t("bookACall.contactInformation")}
            </h3>

            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label={t("bookACall.name")}
                required
                placeholder={t("bookACall.namePlaceholder")}
                error={errors.name?.message}
                {...register("name", { required: t("bookACall.nameRequired") })}
              />
              <Input
                label={t("bookACall.email")}
                type="email"
                required
                placeholder={t("bookACall.emailPlaceholder")}
                error={errors.email?.message}
                {...register("email", {
                  required: t("bookACall.emailRequired"),
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: t("bookACall.invalidEmailAddress"),
                  },
                })}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label={t("bookACall.phone")}
                type="tel"
                required
                placeholder={t("bookACall.phonePlaceholder")}
                error={errors.phone?.message}
                {...register("phone", {
                  required: t("bookACall.phoneRequired"),
                })}
              />
              <Input
                label={t("bookACall.linkedin")}
                placeholder={t("bookACall.linkedinPlaceholder")}
                error={errors.linkedin?.message}
                {...register("linkedin")}
              />
            </div>
          </div>

          {submitError && (
            <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md border border-destructive/20">
              {submitError}
            </div>
          )}

          <div className="pt-4">
            <Button
              type="submit"
              size="lg"
              fullWidth
              loading={isSubmitting}
              className="w-full text-base sm:text-lg h-12"
            >
              {t("bookACall.nextStep")}
            </Button>
            <p className="mt-4 text-center text-sm text-muted-foreground">
              {t("bookACall.submitMessage")}
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BookACall;
